/* eslint-disable no-console */
import { FormErrors, Locales } from "@simplyk/common";
import { v4 } from "uuid";

import { AppFormProvider } from "../../../components/AppProvider/AppFormProvider/AppFormProvider";

import { ZeffyHead } from "@/components/Head/ZeffyHead";
import NotFoundWithProviders from "@/components/NotFound/NotFoundWithProviders";
import { NewsletterFormContainer } from "@/features/NewsletterForm/Signup/NewsletterFormContainer";
import { addCacheTagHeaderToServerResponse } from "@/helpers/cache";
import { getSSRNewsletterForm, getTranslation, provideOrganization } from "@/helpers/getStaticProps";
import { usePageScrollUp } from "@/hooks/usePageScrollUp";
import { useTheme } from "@/hooks/useTheme";
import { ServerSideContext } from "@/types/app";
import { OrganizationOutput, NewsletterFormFrontendPageOutput } from "@/types/trpc";

type NewsletterFormWithOrganization = NewsletterFormFrontendPageOutput & {
  organization: OrganizationOutput;
};

type NewsletterFormPageProps =
  | {
      newsletterForm: NewsletterFormWithOrganization;
      isDeleted?: false;
    }
  | {
      newsletterForm?: null;
      isDeleted: true;
    };

const NewsletterFormPage = ({ newsletterForm, isDeleted }: NewsletterFormPageProps) => {
  usePageScrollUp();

  const theme = useTheme({ color: newsletterForm?.buttonColor, transparent: true });

  if (isDeleted) {
    return (
      <>
        <ZeffyHead />
        <NotFoundWithProviders deletedForm />
      </>
    );
  }

  return (
    <>
      {/* Nothing should wrap or block the Head component */}
      <ZeffyHead title={newsletterForm.title} description="" />
      <AppFormProvider
        theme={theme}
        organization={newsletterForm.organization}
        fields={[{ locale: newsletterForm.locale }]}
        themeColor={newsletterForm.buttonColor}
        isEmbed
        formData={null}
        formType={null}
        SSREnabled={false} // Newsletter has issues with translations when SSR is enabled, do not enable until issue is fixed
      >
        <NewsletterFormContainer newsletterForm={newsletterForm} isEmbedded />
      </AppFormProvider>
    </>
  );
};

export const getServerSideProps = async (context: ServerSideContext<{ newsletterFormId: string }>) => {
  const requestId = (context.req?.headers?.["x-request-id"] as string) || v4();
  const { newsletterFormId } = context.params;

  addCacheTagHeaderToServerResponse(context.res, newsletterFormId);

  const locale = context.locale.toUpperCase() as Locales;

  try {
    const [newsletterFormResponse, translations] = await Promise.all([
      getSSRNewsletterForm({ newsletterFormIdOrUrlPath: newsletterFormId, requestId }),
      getTranslation(context.locale),
    ]);

    if (newsletterFormResponse.error) {
      const code = newsletterFormResponse.error.code;

      if (code === FormErrors.FormDeleted) {
        if (context.res) {
          context.res.statusCode = 410;
        }

        console.log(`Form deleted - redirecting to gone newsletterFormId=${newsletterFormId} ssrId=${requestId}`);
        return {
          props: {
            ...translations,
            isDeleted: true,
          },
        };
      }

      console.log(
        `NEXT_ERROR: form not found - redirecting to notFound newsletterFormId=${newsletterFormId} ssrId=${requestId}`
      );
      return { props: {}, notFound: true };
    }

    return {
      props: {
        ...translations,
        newsletterForm: newsletterFormResponse.data.organization
          ? newsletterFormResponse.data
          : provideOrganization(newsletterFormResponse.data, locale),
      },
    };
  } catch (error) {
    console.log(
      `NEXT_ERROR: request failed - redirecting to notFound newsletterFormId=${newsletterFormId} ssrId=${requestId}`,
      error
    );
    return { props: {}, notFound: true };
  }
};

export default NewsletterFormPage;
